import { Injectable, inject, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthService, OAuthErrorEvent } from 'angular-oauth2-oidc';
import { JwksValidationHandler } from 'angular-oauth2-oidc-jwks';
import { authConfig } from '../config/auth.config';
import { retry } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private oAuthService = inject(OAuthService);
  private router = inject(Router);

  private isAuthenticatedSignal = signal<boolean>(false);
  private isDoneLoadingSignal = signal<boolean>(false);

  public readonly isAuthenticated = this.isAuthenticatedSignal.asReadonly();
  public readonly isDoneLoading = this.isDoneLoadingSignal.asReadonly();

  public readonly userProfile = computed(() => {
    if (!this.isAuthenticated()) return null;
    const claims = this.getIdentityClaims();
    console.log(claims);
    return {
      username: claims?.preferred_username || claims?.name,
      email: claims?.email,
      firstName: claims?.given_name,
      lastName: claims?.family_name,
      fullName: claims?.name
    };
  });

  public readonly userRoles = computed(() => {
    if (!this.isAuthenticated()) return [];
    const claims: any = this.getIdentityClaims();
    return claims?.roles || claims?.realm_access?.roles || [];
  });

  public readonly userGroups = computed(() => {
    if (!this.isAuthenticated()) return [];
    const claims: any = this.getIdentityClaims();
    return claims?.groups || [];
  });

  public readonly isAdmin = computed(() => this.userRoles().includes('ADMIN'));
  public readonly isUser = computed(() => this.userRoles().includes('GVL'));

  constructor() {
    this.setupOAuthEvents();
  }

  private setupOAuthEvents(): void {
    this.oAuthService.events.pipe(retry(5)).subscribe({
      next: (e) => {
        if (e instanceof OAuthErrorEvent) {
          console.error('OAuth Error:', e.reason);
        }

        if (e.type === 'token_received') {
          console.log('Token received - app ready');
          const token = this.oAuthService.getAccessToken();
          localStorage.setItem('authToken', token);
          this.isAuthenticatedSignal.set(true);
        }

        if (e.type === 'logout') {
          this.isAuthenticatedSignal.set(false);
        }
      },
      error: (e) => console.error('OAuth Event Error:', e)
    });
  }

  public async initAuth(): Promise<boolean> {
    this.oAuthService.configure(authConfig);
    this.oAuthService.tokenValidationHandler = new JwksValidationHandler();
    this.oAuthService.setupAutomaticSilentRefresh();

    return new Promise<boolean>((resolve) => {
      this.oAuthService.loadDiscoveryDocumentAndTryLogin().then(() => {
        if (this.oAuthService.hasValidIdToken()) {
          const token = this.oAuthService.getAccessToken();
          localStorage.setItem('authToken', token);
          this.isAuthenticatedSignal.set(true);
          this.isDoneLoadingSignal.set(true);
          resolve(true);
        } else {
          this.isAuthenticatedSignal.set(false);
          this.isDoneLoadingSignal.set(true);
          resolve(false);
        }
      }).catch((error) => {
        console.error('Auth initialization error:', error);
        this.isDoneLoadingSignal.set(true);
        resolve(false);
      });
    });
  }

  public login(targetUrl?: string): void {
    this.oAuthService.initCodeFlow(targetUrl || this.router.url);
  }

  public logout(): void {
    const idToken = this.oAuthService.getIdToken();
    localStorage.removeItem('authToken');

    this.oAuthService.logOut({
      id_token_hint: idToken
    });
  }

  public logoutAndRedirect(redirectPath: string = '/dashboard'): void {
    localStorage.removeItem('authToken');
    this.oAuthService.logOut();
    this.router.navigate([redirectPath]);
  }

  public hasValidToken(): boolean {
    return this.oAuthService.hasValidIdToken();
  }

  public isLoggedIn(): boolean {
    return this.oAuthService.hasValidIdToken();
  }

  public getAccessToken(): string {
    return this.oAuthService.getAccessToken();
  }

  public getIdToken(): string {
    return this.oAuthService.getIdToken();
  }

  public getIdentityClaims(): any {
    return this.oAuthService.getIdentityClaims();
  }

  public getClaim(claim: string): any {
    if (!this.oAuthService.hasValidIdToken()) {
      return '?';
    }

    const claims = this.oAuthService.getIdentityClaims() as any;

    if (!claims) {
      return '??';
    }

    if (claims.hasOwnProperty(claim)) {
      return claims[claim];
    } else {
      console.warn(`Property '${claim}' does not exist for user '${claims?.name}'`);
      return null;
    }
  }

  public hasRole(role: string): boolean {
    return this.userRoles().includes(role);
  }

  public hasGroup(group: string): boolean {
    return this.userGroups().includes(group);
  }

  public isPublicRoute(url: string): boolean {
    const publicRoutes = ['/nutzungsbedingungen', '/datenschutz', '/unauthorized'];
    return publicRoutes.includes(url);
  }
}
